import React, { useEffect, useRef, useState } from "react";
import './Alerts.css';
import { useSelector } from "react-redux";
import { currentSelectedLanguage } from "../../../StateManagement/actions";
import ReactDOM from "react-dom";
import { useLocation } from "react-router-dom";

const DURATION = 10000;

// Global singleton state for alerts across all mounted Alert components
let globalAlerts = [];
const globalTimers = {};
const remainingTimes = {};
const startedAtTimes = {};
const subscribers = new Map();
const mountedInstances = new Set();
let activeRendererId = null;
let globalCurrentPathname = window.location.pathname;
let globalLastResetTrigger = null;

const isLoginPage = (pathname) => {
  const loginPaths = ["/", "/ssosignin", "/authenticating", "/rdocsprepare", "/launch"];
  return loginPaths.includes(pathname);
};

const notifyActiveRenderer = () => {
  if (activeRendererId && subscribers.has(activeRendererId)) {
    subscribers.get(activeRendererId)([...globalAlerts]);
  }
  // All other instances must render empty list to avoid duplicates
  subscribers.forEach((callback, id) => {
    if (id !== activeRendererId) {
      callback([]);
    }
  });
};

const removeAlert = (id) => {
  if (globalTimers[id]) {
    clearTimeout(globalTimers[id]);
    delete globalTimers[id];
  }
  delete remainingTimes[id];
  delete startedAtTimes[id];
  globalAlerts = globalAlerts.filter((a) => a.id !== id);
  notifyActiveRenderer();
};

const startCountdownForAlert = (id, ms) => {
  if (globalTimers[id]) {
    clearTimeout(globalTimers[id]);
  }
  startedAtTimes[id] = Date.now();
  remainingTimes[id] = ms;
  globalTimers[id] = setTimeout(() => {
    removeAlert(id);
  }, ms);
};

const pauseAlertTimer = (id) => {
  if (!globalTimers[id]) return;
  clearTimeout(globalTimers[id]);
  delete globalTimers[id];

  const elapsed = Date.now() - startedAtTimes[id];
  remainingTimes[id] = Math.max(0, remainingTimes[id] - elapsed);

  globalAlerts = globalAlerts.map((a) => (a.id === id ? { ...a, isPaused: true } : a));
  notifyActiveRenderer();
};

const resumeAlertTimer = (id) => {
  globalAlerts = globalAlerts.map((a) => (a.id === id ? { ...a, isPaused: false } : a));

  const remaining = remainingTimes[id];
  if (remaining > 0) {
    startCountdownForAlert(id, remaining);
  } else {
    removeAlert(id);
  }
  notifyActiveRenderer();
};

export default function Alert(props) {
  const selectedLanguage = useSelector(currentSelectedLanguage);
  const location = useLocation();

  const [localAlerts, setLocalAlerts] = useState([]);
  const [container, setContainer] = useState(null);
  const instanceId = useRef(Math.random() + "-" + Math.random());

  useEffect(() => {
    if (globalCurrentPathname !== location.pathname) {
      globalCurrentPathname = location.pathname;
      globalLastResetTrigger = null;

      const now = Date.now();
      const alertsToRemove = [];

      globalAlerts = globalAlerts.map((alert) => {
        const startedAt = startedAtTimes[alert.id];
        if (startedAt) {
          const newTransitions = (alert.transitions || 0) + 1;
          const maxAllowed = isLoginPage(alert.createdOnPathname) ? 1 : 0;

          if (newTransitions > maxAllowed) {
            alertsToRemove.push(alert.id);
          }
          return { ...alert, transitions: newTransitions };
        }
        return alert;
      });

      if (alertsToRemove.length > 0) {
        alertsToRemove.forEach((id) => {
          if (globalTimers[id]) {
            clearTimeout(globalTimers[id]);
            delete globalTimers[id];
          }
          delete remainingTimes[id];
          delete startedAtTimes[id];
        });
        globalAlerts = globalAlerts.filter((a) => !alertsToRemove.includes(a.id));
        notifyActiveRenderer();
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    if (props.resetTrigger !== undefined && props.resetTrigger !== null) {
      if (globalLastResetTrigger !== null && globalLastResetTrigger !== props.resetTrigger) {
        Object.keys(globalTimers).forEach((id) => {
          if (globalTimers[id]) {
            clearTimeout(globalTimers[id]);
            delete globalTimers[id];
          }
        });
        Object.keys(remainingTimes).forEach((id) => delete remainingTimes[id]);
        Object.keys(startedAtTimes).forEach((id) => delete startedAtTimes[id]);
        globalAlerts = [];
        notifyActiveRenderer();
      }
      globalLastResetTrigger = props.resetTrigger;
    }
  }, [props.resetTrigger]);

  useEffect(() => {
    const id = instanceId.current;
    mountedInstances.add(id);
    if (!activeRendererId) {
      activeRendererId = id;
    }

    subscribers.set(id, (alertsList) => {
      setLocalAlerts(alertsList);
    });

    let el = document.getElementById("rdocs-global-alerts-container");
    if (!el) {
      el = document.createElement("div");
      el.id = "rdocs-global-alerts-container";
      el.className = "alert-container";
      document.body.appendChild(el);
    }
    setContainer(el);

    notifyActiveRenderer();

    return () => {
      subscribers.delete(id);
      mountedInstances.delete(id);
      if (activeRendererId === id) {
        if (mountedInstances.size > 0) {
          activeRendererId = Array.from(mountedInstances)[0];
        } else {
          activeRendererId = null;
        }
      }
      notifyActiveRenderer();
    };
  }, []);

  useEffect(() => {
    if (props.alertType && (props.alertMsg || props.alertType === "sucess" || props.alertType === "success")) {
      const normalizedType = (props.alertType === "success" || props.alertType === "sucess") ? "sucess" : props.alertType;
      const alertMsg = props.alertMsg || "";

      const existingIndex = globalAlerts.findIndex(
        (a) => a.type === normalizedType && a.msg === alertMsg
      );

      const newId = Date.now() + "-" + Math.random();

      if (existingIndex !== -1) {
        const existing = globalAlerts[existingIndex];
        if (globalTimers[existing.id]) {
          clearTimeout(globalTimers[existing.id]);
          delete globalTimers[existing.id];
        }
        delete remainingTimes[existing.id];
        delete startedAtTimes[existing.id];
        globalAlerts.splice(existingIndex, 1);
      }

      const newAlert = {
        id: newId,
        type: normalizedType,
        msg: alertMsg,
        isfromSeetting: props.isfromSeetting,
        isPaused: false,
        createdOnPathname: window.location.pathname
      };

      globalAlerts = [newAlert, ...globalAlerts];
      if (globalAlerts.length > 3) {
        const oldest = globalAlerts.pop();
        if (globalTimers[oldest.id]) {
          clearTimeout(globalTimers[oldest.id]);
          delete globalTimers[oldest.id];
        }
        delete remainingTimes[oldest.id];
        delete startedAtTimes[oldest.id];
      }

      startCountdownForAlert(newId, DURATION);
      notifyActiveRenderer();

      // Reset parent's alert props if functions are provided
      if (props.setAlertMsg) props.setAlertMsg("");
      if (props.setAlertType) props.setAlertType("");
    }
  }, [props.alertType, props.alertMsg, props.isfromSeetting]);

  const customClass = props.class || props.className || "";

  if (!container || activeRendererId !== instanceId.current) return null;

  return ReactDOM.createPortal(
    <>
      {localAlerts.map((alert) => {
        const isPaused = alert.isPaused;
        const handleClose = () => removeAlert(alert.id);
        const pauseTimer = () => pauseAlertTimer(alert.id);
        const resumeTimer = () => resumeAlertTimer(alert.id);

        return (
          <React.Fragment key={alert.id}>
            {alert.type === "error" && (
              <div
                id="alertDanger"
                className={`${customClass} alert alert-danger alertDanger errorborder alertZindex ${isPaused ? "alert-paused" : ""}`}
                onMouseEnter={pauseTimer}
                onMouseLeave={resumeTimer}>
                <div className="alert-header">
                  <span className="material-icons-round me-2 erroricon">
                    warning
                  </span>
                  <div className="alert-content">
                    <strong
                      className="me-2 font-180"
                      style={{ color: "#bc272e" }}>
                      {selectedLanguage.LstLanguageCodes.UI_Error}!
                    </strong>
                    <p className="mb-0 dangertext">
                      {alert.msg}
                    </p>
                  </div>
                </div>
                <button type="button" className="custom-close-btn" onClick={handleClose}></button>
                <div className="alert-progress error-progress "></div>
              </div>
            )}
            {alert.type === "warning" && (
              <div
                id="alertDanger"
                className={`${customClass} alert alert-danger alertDanger dangerborder  alertZindex ${isPaused ? "alert-paused" : ""}`}
                onMouseEnter={pauseTimer}
                onMouseLeave={resumeTimer}>
                <div className="alert-header">
                  <span className="material-icons me-2 warningicon">error</span>
                  <div className="alert-content">
                    <strong className="me-2 font-180" style={{ color: "#c09853" }}>{selectedLanguage.LstLanguageCodes.UI_Warning}!
                    </strong>
                    <p className="mb-0 dangertext"> {alert.msg}</p>
                  </div>
                </div>
                <button type="button" className="custom-close-btn" onClick={handleClose}></button>
                <div className="alert-progress warning-progress"></div>
              </div>
            )}
            {alert.type === "sucess" && alert.isfromSeetting != "true" && (
              <div
                id="alertSucess"
                className={`${customClass} alert alert-danger alertSuccess alertZindex ${isPaused ? "alert-paused" : ""}`}
                onMouseEnter={pauseTimer}
                onMouseLeave={resumeTimer}>
                <div className="alert-header">
                  <span className="material-icons me-2 successicon"> check_circle</span>
                  <div className="alert-content">
                    <strong className="me-2 font-180" style={{ color: "#3ab16d" }}> {selectedLanguage.LstLanguageCodes.UI_Success}!
                    </strong>
                    <p className="mb-0 dangertext"> {alert.msg || selectedLanguage.LstLanguageCodes.UI_YouraccountHasbeen}</p>
                  </div>
                </div>
                <button type="button" className="custom-close-btn" onClick={handleClose}></button>
                <div className="alert-progress success-progress"></div>
              </div>
            )}
            {alert.type === "sucess" && alert.isfromSeetting == "true" && (
              <div
                id="alertSucess"
                className={`${customClass} alert alert-danger alertSuccess alertZindex ${isPaused ? "alert-paused" : ""
                  }`}
                onMouseEnter={pauseTimer}
                onMouseLeave={resumeTimer}>
                <div className="alert-header">
                  <span className="material-icons me-2 successicon">check_circle</span>
                  <div className="alert-content">
                    <strong className="me-2 font-180" style={{ color: "#3ab16d" }}> {selectedLanguage.LstLanguageCodes.UI_Success}!
                    </strong>
                    <p className="mb-0 dangertext"> {alert.msg || selectedLanguage.LstLanguageCodes.UI_SettingsSavedSucessFullMsg} </p>
                  </div></div>
                <button type="button" className="custom-close-btn" onClick={handleClose}></button>
                <div className="alert-progress success-progress"></div>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </>,
    container
  );
}